/* eslint-disable */
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellAvatar, {
  AVATAR_MARGIN
} from "@saleor/components/TableCellAvatar";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, renderCollection } from "../../../misc";
import { ListActions, ListProps2 } from "../../../types";
// import { SaleDetails_sale } from "../../types/SaleDetails";
// import { VoucherDetails_voucher } from "../../types/VoucherDetails";
import { Producttag_producttag } from "../../../producttags/types/Producttag";

export interface SaleVariantsProps extends ListProps2, ListActions {
  discount: Producttag_producttag;
  onVariantAssign: () => void;
  onVariantUnassign: (id: string) => void;
}

const useStyles = makeStyles(
  theme => ({
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    colName: {
      paddingLeft: 0,
      width: 300
    },
    colNameLabel: {
      marginLeft: AVATAR_MARGIN + theme.spacing(3)
    },
    colPublished: {
      width: 150
    },
    colType: {
      width: 200
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "DiscountVariants" }
);

const numberOfColumns = 6;

const DiscountVariants: React.FC<SaleVariantsProps> = props => {
  const {
    discount: sale,

    disabled,
    pageInfo,
    onRowClick,
    onPreviousPage,
    onVariantAssign,
    onVariantUnassign,
    onNextPage,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar
  } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Eligible SKU",
          description: "section header"
        })}
        toolbar={
          <Button color="primary" onClick={onVariantAssign}>
            <FormattedMessage
              defaultMessage="Assign SKU"
              description="button"
            />
          </Button>
        }
      />
      <ResponsiveTable>
        <colgroup>
          <col />
          <col className={classes.colName} />
          <col className={classes.colType} />
          <col className={classes.colType} />
          <col className={classes.colPublished} />
          <col className={classes.colActions} />
        </colgroup>
        <TableHead
          colSpan={numberOfColumns}
          selected={selected}
          disabled={disabled}
          items={maybe(() => sale.variants.edges.map(edge => edge.node))}
          toggleAll={toggleAll}
          toolbar={toolbar}
        >
          <TableCell className={classes.colName}>
            <span className={classes.colNameLabel}>
              <FormattedMessage defaultMessage="Product Name" />
            </span>
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="SKU" />
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="SKU Name " />
          </TableCell>
          <TableCell className={classes.colPublished}>
            <FormattedMessage
              defaultMessage="Published"
              description="product is published"
            />
          </TableCell>
          <TableCell className={classes.colActions} />
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {renderCollection(
            maybe(() => sale.variants.edges.map(edge => edge.node)),
            variant => {
              const isSelected = variant ? isChecked(variant.id) : false;
              return (
                <TableRow
                  hover={!!variant}
                  key={variant ? variant.id : "skeleton"}
                  onClick={
                    variant && onRowClick(variant.product.id, variant.id)
                  }
                  className={classes.tableRow}
                  selected={isSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      disabled={disabled}
                      disableClickPropagation
                      onChange={() => toggle(variant.id)}
                    />
                  </TableCell>
                  <TableCellAvatar
                    className={classes.colName}
                    thumbnail={maybe(() => variant.thumbnail.url)}
                  >
                    {maybe<React.ReactNode>(
                      () => variant.product.name,
                      <Skeleton />
                    )}
                  </TableCellAvatar>
                  <TableCell className={classes.colType}>
                    {maybe<React.ReactNode>(() => variant.sku, <Skeleton />)}
                  </TableCell>
                  <TableCell className={classes.colType}>
                    {maybe<React.ReactNode>(
                      () => variant.skuName,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colPublished}>
                    {variant && variant.isPublished !== undefined ? (
                      <StatusLabel
                        label={
                          variant.isPublished
                            ? intl.formatMessage({
                                defaultMessage: "Published",
                                description: "sku is published"
                              })
                            : intl.formatMessage({
                                defaultMessage: "Not published",
                                description: "sku is not published"
                              })
                        }
                        status={variant.isPublished ? "success" : "error"}
                      />
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colActions}>
                    <IconButton
                      disabled={!variant || disabled}
                      onClick={event => {
                        event.stopPropagation();
                        onVariantUnassign(variant.id);
                      }}
                    >
                      <DeleteIcon color="primary" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            },
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  <FormattedMessage defaultMessage="No sku found" />
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </ResponsiveTable>
    </Card>
  );
};
DiscountVariants.displayName = "DiscountVariants";
export default DiscountVariants;
